import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from '@angular/core';
import { CertificatesService } from '../../../../../core/services/api/certificates.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  from,
  map,
  throwError,
} from 'rxjs';
import {
  CertificateButton,
  Certificate,
  SimpleListRowDirections,
} from '../../../../../core/models/certificate';
import {
  CertificateDynamicSectionContentType,
  CertificateStatus,
  LingAttributeIconKey,
  LingAttributeType,
} from '../../../../../core/enums';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DialogParams } from '../../../../../core/models/dialog-params';
import { CertificateCreateRequest } from '../../../../../core/models/certificate-create-request';
import { CommonModule } from '@angular/common';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
  MatTooltipModule,
} from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { QRCodeModule } from 'angularx-qrcode';
import { MediaSliderComponent } from '../media-slider/media-slider.component';
import { SnackBarService } from '../../../../../core/services/snack-bar.service';
import {
  copyImageToBuffer,
  getCertificatePreviewLink,
  parseLinksHtml,
} from '../../../../../core/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ParsedCertificatePageConfigProfile } from '../../../../../core/models/certificate-configuration';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DialogService } from '../../../../../core/services/dialog.service';
import { environment } from '../../../../../../environments/environment';
import { RouterEventsService } from '../../../../../core/services/router-events.service';
import { FeathersService } from '../../../../../core/services/api/feathers.service';
import { CertificateDraftsService } from '../../../../../core/services/api/certificate-drafts.service';
import { CertificateDraft } from '../../../../../core/models/certificate-draft';
import { Router, RouterModule } from '@angular/router';
import { linIcon } from '../../../../../core/services/mat-icon-registry.service';

const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  disableTooltipInteractivity: true,
};

@UntilDestroy()
@Component({
  selector: 'ling-certificate-view',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    QRCodeModule,
    MediaSliderComponent,
    TranslateModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    RouterModule,
  ],
  templateUrl: './certificate-view.component.html',
  styleUrls: ['./certificate-view.component.scss'],
  providers: [
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificateViewComponent implements OnInit, OnChanges {
  @HostBinding('class.live-mode') get isLiveMode() {
    return this.liveMode || this.isPublicPreview;
  }

  protected readonly certificateDynamicSectionContentType =
    CertificateDynamicSectionContentType;
  protected readonly simpleListRowDirections = SimpleListRowDirections;
  protected readonly getCertificatePreviewLink = getCertificatePreviewLink;

  certificateStatus = CertificateStatus;
  initialLoading = false;
  loadingFailedText = '';
  qrUrl?: SafeUrl;
  elementRef = inject(ElementRef);

  @Input() set stylesProfile(
    profile: ParsedCertificatePageConfigProfile | null | undefined
  ) {
    this.stylesProfile$.next(profile ?? null);
  }

  protected readonly stylesProfile$ =
    new BehaviorSubject<ParsedCertificatePageConfigProfile | null>(null);

  @Input('certificate') set certificateInput(
    certificate: Certificate | null | undefined
  ) {
    this.certificate$.next(certificate);
  }

  @Input() loginProcessMessage = false;
  certificate$ = new BehaviorSubject<Certificate | null | undefined>(null);
  certificateVM$ = this.certificate$.pipe(
    map((cert) => {
      if (!cert) {
        return cert;
      }

      const parsedCert = { ...cert };

      if (typeof parsedCert.description === 'string') {
        parsedCert.description = parseLinksHtml(
          parsedCert.description ?? '',
          this.sanitizer
        ) as string;
      }

      const parsedAttributeTypes = [
        LingAttributeType.Text,
        LingAttributeType.ShortText,
      ];
      parsedCert.attributes = parsedCert.attributes?.map((attribute) => {
        let mappedAttribute = { ...attribute };

        if (parsedAttributeTypes.includes(attribute.type)) {
          mappedAttribute = {
            ...mappedAttribute,
            value: parseLinksHtml(attribute.value, this.sanitizer),
          };
        }

        return mappedAttribute;
      });

      return parsedCert;
    })
  );
  @Input() liveMode: boolean = false;
  @Input() isPublicPreview: boolean = false;

  private feathers = inject(FeathersService);
  private certificateDraftsService = inject(CertificateDraftsService);
  public router = inject(Router);

  isOwner$ = combineLatest([
    this.certificate$,
    this.feathers.me$,
  ]).pipe(
    map(
      ([cert, me]) =>
        !!cert && !!me && !!(cert as Certificate).owner?._id && (cert as Certificate).owner?._id === me._id
    )
  );

  private refreshDraftTrigger$ = new BehaviorSubject(0);

  draftForCertificate$ = combineLatest([
    this.certificate$,
    this.refreshDraftTrigger$,
  ]).pipe(
    map(([cert]) => {
      if (!cert?.activeDraft || !cert?.guestEditToken) {
        return null;
      }
      return {
        _id: cert.activeDraft,
        guestEditToken: cert.guestEditToken,
        draftStatus: 'active' as const,
      } as CertificateDraft;
    })
  );

  constructor(
    public certificatesService: CertificatesService,
    protected sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private message: SnackBarService,
    private translateService: TranslateService,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public dialogParams: DialogParams<{
      certificate: string | Certificate | CertificateCreateRequest;
    }>,
    public dialog: DialogService,
    public routerEventsService: RouterEventsService
  ) {}

  certificateLinksIcons: {
    [key in LingAttributeIconKey]: string;
  } = {
    [LingAttributeIconKey.Youtube]: linIcon.Youtube,
    [LingAttributeIconKey.Facebook]: linIcon.Facebook,
    [LingAttributeIconKey.Instagram]: linIcon.Instagram,
    [LingAttributeIconKey.LinkedIn]: linIcon.LinkedIn,
    [LingAttributeIconKey.File]: linIcon.File,
    [LingAttributeIconKey.URL]: linIcon.Link,
    [LingAttributeIconKey.Code]: linIcon.Code,
    [LingAttributeIconKey.Text]: linIcon.Text,
  };

  sanitizeMarkdownHtml(html: string): SafeHtml {
    if (!html) {
      return '';
    }
    // Allow HTML with tables and video
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  ngOnInit(): void {
    this.initCustomStylesListener();
    if (!this.dialogParams?.data?.certificate) {
      return;
    }
    const certificate =
      this.dialogParams.data?.certificate ?? this.certificate$;
    const id = typeof certificate === 'string' ? certificate : '';
    if (certificate && typeof certificate !== 'string') {
      this.certificate$.next(certificate as Certificate);
      this.cd.detectChanges();
    }

    if (id) {
      this.initialLoading = true;
      this.certificatesService
        .watchCertificate(id)
        .pipe(
          untilDestroyed(this),
          catchError((err) => {
            this.initialLoading = false;
            this.loadingFailedText = this.translateService.instant(
              'certificateView.certificateLoadFail'
            );
            return throwError(err);
          })
        )
        .subscribe((cert) => {
          this.certificate$.next(cert);
          this.initialLoading = false;
          this.cd.detectChanges();
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const certificate =
      this.certificate$.value ?? this.dialogParams?.data?.certificate;
    if (certificate && typeof certificate !== 'string') {
      this.certificate$.next(certificate as Certificate);
      this.cd.detectChanges();
    }
    this.cd.detectChanges();
  }

  initCustomStylesListener() {
    const defaultStyles = getComputedStyle(this.elementRef.nativeElement);
    const defaultValues = {
      '--cert-section-title-color': defaultStyles.getPropertyValue(
        '--cert-section-title-color'
      ),
      '--cert-background-color': defaultStyles.getPropertyValue(
        '--cert-background-color'
      ),
      '--cert-border-color': defaultStyles.getPropertyValue(
        '--cert-border-color'
      ),
      '--cert-text-color': defaultStyles.getPropertyValue('--cert-text-color'),
    } as const;
    this.stylesProfile$.pipe(untilDestroyed(this)).subscribe((profile) => {
      this.elementRef.nativeElement.style.setProperty(
        '--cert-section-title-color',
        profile?.sectionTitleColor ??
          defaultValues['--cert-section-title-color']
      );

      this.elementRef.nativeElement.style.setProperty(
        '--cert-background-color',
        profile?.certificateBackgroundColor ??
          defaultValues['--cert-background-color']
      );

      this.elementRef.nativeElement.style.setProperty(
        '--cert-border-color',
        profile?.dividersColor ?? defaultValues['--cert-border-color']
      );

      this.elementRef.nativeElement.style.setProperty(
        '--cert-text-color',
        profile?.textColor ?? defaultValues['--cert-text-color']
      );
    });
  }

  copyLink(certificateId: string = '', successMessageLocaleKey: string): void {
    const link = `${environment.frontUrl}/preview/${certificateId}`;

    from(navigator.clipboard.writeText(link)).subscribe(() => {
      this.message.success(
        this.translateService.instant(successMessageLocaleKey)
      );
    });
  }

  copyImage(safeUrl?: SafeUrl): void {
    copyImageToBuffer(safeUrl)?.subscribe({
      next: () => {
        this.message.success(
          this.translateService.instant('snackbar.success.qrCodeCopied')
        );
      },
      error: () => {
        this.message.warn(
          this.translateService.instant('snackbar.warn.imageCopyNotSupported')
        );
      },
    });
  }

  getGuestEditLink(token: string): string {
    return `${environment.frontUrl}/edit/${token}`;
  }

  generateAndCopyGuestLink(certificate: Certificate): void {
    if (!certificate._id) {
      return;
    }
    this.certificateDraftsService
      .createDraft(certificate._id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (draft) => {
          const link = this.getGuestEditLink(draft.guestEditToken);
          from(navigator.clipboard.writeText(link)).subscribe(() => {
            this.message.success(
              this.translateService.instant('snackbar.success.guestEditLinkCopied')
            );
            const current = this.certificate$.value;
            if (current) {
              this.certificate$.next({
                ...current,
                activeDraft: draft._id,
                guestEditToken: draft.guestEditToken,
              } as Certificate);
            }
            this.refreshDraftTrigger$.next(this.refreshDraftTrigger$.value + 1);
            this.cd.markForCheck();
          });
        },
        error: (err) => {
          this.message.error(
            err?.message ||
              this.translateService.instant('guestEdit.generateFailed')
          );
        },
      });
  }

  copyGuestLink(draft: CertificateDraft): void {
    const link = this.getGuestEditLink(draft.guestEditToken);
    from(navigator.clipboard.writeText(link)).subscribe(() => {
      this.message.success(
        this.translateService.instant('snackbar.success.guestEditLinkCopied')
      );
    });
  }

  getReviewChangesRoute(certificateId: string): string[] {
    return ['/certificates', 'edit', certificateId];
  }

  getReviewChangesQueryParams(draftId: string): { reviewDraft: string } {
    return { reviewDraft: draftId };
  }

  asLingAttributeIconKey(iconKey: string) {
    return iconKey as LingAttributeIconKey;
  }

  getCertificateButtonUrl(button: CertificateButton): string {
    return button.finalUrl || button.baseUrl;
  }
}
